import { Conversation, Participant, ParticipantUpdateReason } from '@twilio/conversations';
import Cookies from 'js-cookie';
import uniqBy from 'lodash/uniqBy';
import { useEffect, useRef, useState } from 'react';

import { getConversation } from '../../services/communication/getConversation';
import { getParticipant } from '../../services/communication/getParticipantBySid';
import { IGMConversationParticipant } from '../../services/communication/types/IGMConversationParticipant';

interface IUseConversationParticipants {
  participants: IGMConversationParticipant[];
  status: 'initializing' | 'failed' | 'ready' | 'loading';
  participantJoin: string;
  typingParticipants: string[];
}

export default function useConversationParticipants(
  twilioConversation: Conversation | null,
): IUseConversationParticipants {
  const jwtToken = Cookies.get('jwtToken');
  const [participants, setParticipants] = useState<IGMConversationParticipant[]>([]);
  const [status, setStatus] = useState<IUseConversationParticipants['status']>('initializing');
  const [participantJoin, setParticipantJoin] = useState<string>('');
  const [typingParticipants, setTypingParticipants] = useState<string[]>([]);

  // Track fallback typing timers
  const typingTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    if (!twilioConversation) {
      setParticipants([]);
      setStatus('ready');
      return;
    }

    setStatus('loading');

    getConversation(twilioConversation.sid, true).then((currentConversation) => {
      setParticipants(currentConversation.participants || []);
      setStatus('ready');
    });
  }, [twilioConversation, jwtToken]);

  const onParticipantJoined = async (participant: Participant): Promise<void> => {
    if (!twilioConversation) return;

    setParticipantJoin(participant.sid);

    const newParticipant = await getParticipant(twilioConversation.sid, participant.sid);
    setParticipants((prevParticipants) =>
      uniqBy([...prevParticipants, newParticipant], 'sid'),
    );
  };

  const onParticipantLeft = (participant: Participant): void => {
    setParticipants((prevParticipants) =>
      prevParticipants.filter((p) => p.sid !== participant.sid),
    );
    setTypingParticipants((prev) => prev.filter((id) => id !== participant.identity));

    // Clean up any typing timeout
    const identity = participant.identity;
    if (identity && typingTimeoutsRef.current[identity]) {
      clearTimeout(typingTimeoutsRef.current[identity]);
      delete typingTimeoutsRef.current[identity];
    }
  };

  const onParticipantUpdated = (data: {
    participant: Participant;
    updateReasons: ParticipantUpdateReason[];
  }): void => {
    console.warn(`Participant ${data.participant.sid} has been updated`);
    // Optional: update participant info in state if needed
  };

  const onTypingStarted = (participant: Participant): void => {
    const identity = participant.identity;
    if (!identity) return;

    setTypingParticipants((prev) =>
      prev.includes(identity) ? prev : [...prev, identity],
    );

    // Clear any existing timeout for this identity
    if (typingTimeoutsRef.current[identity]) {
      clearTimeout(typingTimeoutsRef.current[identity]);
    }

    // Set a new fallback timeout (10 seconds)
    typingTimeoutsRef.current[identity] = setTimeout(() => {
      setTypingParticipants((prev) => prev.filter((id) => id !== identity));
      delete typingTimeoutsRef.current[identity];
    }, 10000);
  };

  const onTypingEnded = (participant: Participant): void => {
    const identity = participant.identity;
    if (!identity) return;
  
    // Delay removal by 5 seconds
    typingTimeoutsRef.current[identity] = setTimeout(() => {
      setTypingParticipants((prev) => prev.filter((id) => id !== identity));
      delete typingTimeoutsRef.current[identity];
    }, 5000); // 5 seconds
  };

  useEffect(() => {
    if (!twilioConversation) return () => {};

    twilioConversation.on('participantJoined', onParticipantJoined);
    twilioConversation.on('participantLeft', onParticipantLeft);
    twilioConversation.on('participantUpdated', onParticipantUpdated);
    twilioConversation.on('typingStarted', onTypingStarted);
    twilioConversation.on('typingEnded', onTypingEnded);

    return () => {
      // Cleanup all timers
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};

      // Remove event listeners
      twilioConversation.off('participantJoined', onParticipantJoined);
      twilioConversation.off('participantLeft', onParticipantLeft);
      twilioConversation.off('participantUpdated', onParticipantUpdated);
      twilioConversation.off('typingStarted', onTypingStarted);
      twilioConversation.off('typingEnded', onTypingEnded);
    };
  }, [twilioConversation]);

  return {
    status,
    participants,
    participantJoin,
    typingParticipants,
  };
}